import { createContext, useContext, useState } from 'react';

const TutorialContext = createContext();

export function TutorialProvider({ children }) {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [currentPage, setCurrentPage] = useState('');
  const [forceShow, setForceShow] = useState(false);

  const startTutorial = (pageSteps, pageName) => {
    setSteps(pageSteps);
    setCurrentPage(pageName);
    setCurrentStep(0);
    setIsActive(true);
    setForceShow(false);
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipTutorial = async () => {
    const pageKey = currentPage || window.location.pathname;

    // For public pages, use localStorage (no auth)
    const token = localStorage.getItem('token');
    if (!token) {
      localStorage.setItem(`tutorial_skipped_${pageKey}`, 'true');
    } else {
      // For logged in users, use API
      try {
        const API_URL = import.meta.env.PROD
          ? 'https://progress-tracker-api.syafri-tuloli.workers.dev/api'
          : '/api';

        await fetch(`${API_URL}/tutorial/skip`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ page: pageKey }),
        });
      } catch (error) {
        console.error('Failed to skip tutorial:', error);
      }
    }

    setIsActive(false);
    setCurrentStep(0);
    setSteps([]);
  };

  const completeTutorial = async () => {
    const pageKey = currentPage || window.location.pathname;

    // For public pages, use localStorage (no auth)
    const token = localStorage.getItem('token');
    if (!token) {
      localStorage.setItem(`tutorial_completed_${pageKey}`, 'true');
    } else {
      // For logged in users, use API
      try {
        const API_URL = import.meta.env.PROD
          ? 'https://progress-tracker-api.syafri-tuloli.workers.dev/api'
          : '/api';

        await fetch(`${API_URL}/tutorial/complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ page: pageKey }),
        });
      } catch (error) {
        console.error('Failed to complete tutorial:', error);
      }
    }

    setIsActive(false);
    setCurrentStep(0);
    setSteps([]);
  };

  const resetTutorial = async (stepsToRestart, pageName) => {
    const pageKey = pageName || currentPage || window.location.pathname;
    const token = localStorage.getItem('token');

    if (!token) {
      // For public pages, clear localStorage
      localStorage.removeItem(`tutorial_skipped_${pageKey}`);
      localStorage.removeItem(`tutorial_completed_${pageKey}`);

      // If steps provided, restart immediately
      if (stepsToRestart) {
        setSteps(stepsToRestart);
        setCurrentPage(pageName);
        setCurrentStep(0);
        setIsActive(true);
      } else {
        setForceShow(true);
      }
    } else {
      // For logged in users, use API
      try {
        const API_URL = import.meta.env.PROD
          ? 'https://progress-tracker-api.syafri-tuloli.workers.dev/api'
          : '/api';

        await fetch(`${API_URL}/tutorial/reset`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        // If steps provided, restart immediately
        if (stepsToRestart) {
          setSteps(stepsToRestart);
          setCurrentPage(pageName);
          setCurrentStep(0);
          setIsActive(true);
        } else {
          setForceShow(true);
        }
      } catch (error) {
        console.error('Failed to reset tutorial:', error);
      }
    }
  };

  const closeTutorial = () => {
    setIsActive(false);
    setCurrentStep(0);
    setSteps([]);
  };

  return (
    <TutorialContext.Provider
      value={{
        isActive,
        currentStep,
        steps,
        currentPage,
        forceShow,
        startTutorial,
        nextStep,
        prevStep,
        skipTutorial,
        completeTutorial,
        resetTutorial,
        closeTutorial,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial() {
  const context = useContext(TutorialContext);
  return context;
}
